import { useMemo } from 'react';
import { useApolloClient } from '@frameio/graphql-client';
import type { QuickSearchResult } from '@frameio/graphql-client/generated';
import { GetQuickSearchResultsNlp } from '../../gql/generated/GetQuickSearchResultsNlp';
import {
  GetQuickSearchResultsV2,
  type GetQuickSearchResultsV2QueryVariables,
} from '../../gql/generated/GetQuickSearchResultsV2';
import type { ListMode } from '../../hooks/useQuickSearchListMode';

type UseInitialSearchNodesProps = {
  requestVariables: GetQuickSearchResultsV2QueryVariables;
  skip: boolean;
  isInitial: boolean;
  listMode: ListMode;
};

type UseInitialSearchNodesReturn = {
  initialLexicalNodes: QuickSearchResult[] | undefined;
  initialNlpNodes: QuickSearchResult[] | undefined;
};

/**
 * Hook to read cached search results from Apollo cache for machine initialization.
 * Reads lexical nodes (always) and optionally NLP nodes (if listMode is not 'SINGLE').
 *
 * @param requestVariables - The GraphQL query variables
 * @param skip - Whether to skip reading from cache
 * @param isInitial - Whether this is the initial render
 * @param listMode - The list mode ('SINGLE', 'MULTI', or 'MULTI_TABBED') - determines if NLP nodes are read
 * @returns Object with initialLexicalNodes and initialNlpNodes (undefined if listMode is 'SINGLE')
 */
export const useInitialSearchNodes = ({
  requestVariables,
  skip,
  isInitial,
  listMode,
}: UseInitialSearchNodesProps): UseInitialSearchNodesReturn => {
  const { query } = requestVariables;
  const apolloClient = useApolloClient();
  const { cache } = apolloClient;

  const hasDefinedQuery = query !== undefined && query.trim() !== '';

  const initialLexicalNodes = useMemo<QuickSearchResult[] | undefined>(() => {
    if (!isInitial || skip || !hasDefinedQuery) return undefined;

    try {
      const cachedData = cache.readQuery({
        query: GetQuickSearchResultsV2,
        variables: requestVariables,
      });

      return (cachedData?.account?.quickSearch?.nodes ??
        []) as QuickSearchResult[];
    } catch {
      return undefined;
    }
  }, [isInitial, skip, cache, requestVariables, hasDefinedQuery]);

  const initialNlpNodes = useMemo<QuickSearchResult[] | undefined>(() => {
    if (listMode === 'SINGLE' || !isInitial || skip || !hasDefinedQuery)
      return undefined;

    try {
      const cachedData = cache.readQuery({
        query: GetQuickSearchResultsNlp,
        variables: requestVariables,
      });

      return (cachedData?.account?.quickSearchNlp?.nodes ??
        []) as QuickSearchResult[];
    } catch {
      return undefined;
    }
  }, [listMode, isInitial, skip, cache, requestVariables, hasDefinedQuery]);

  return {
    initialLexicalNodes,
    initialNlpNodes,
  };
};
